import { ChangeDetectionStrategy, Component, computed, HostListener, inject, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { environment } from '@work-frontend-monorepo/work-environments';
import { WorkEnvironmentInfoDto, WorkEnvironmentService } from '@work-frontend-monorepo/work-rest-api';

interface EnvironmentAppLink {
  label: string;
  url: string;
}

/**
 * Globaler Hinweis-Ribbon oben links, der in allen Applikationen identisch angezeigt wird,
 * sobald die App NICHT auf der Produktion läuft (Test, Localhost, ...).
 *
 * Über den Info-Button lässt sich ein Modal öffnen, das erklärt wozu die Umgebung dient,
 * die übrigen Apps derselben Umgebung verlinkt und zeigt, wohin E-Mails und SMS umgeleitet werden.
 *
 * Einbindung: einmalig im Root-Template der jeweiligen App platzieren.
 */
@Component({
  selector: 'work-ui-environment-ribbon',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './environment-ribbon.component.html',
  styleUrl: './environment-ribbon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnvironmentRibbonComponent {
  /**
   * Auf der Produktion wird nichts gerendert.
   */
  protected readonly isVisible = environment.name !== 'production';

  /**
   * Kurzbezeichnung der Umgebung, z.B. `TEST` oder `LOCAL`.
   */
  protected readonly environmentLabel = environment.name === 'localhost' ? 'LOCAL' : (environment.name || 'UNBEKANNT').toUpperCase();

  /**
   * Alle Apps derselben Umgebung, für die eine URL konfiguriert ist.
   */
  protected readonly appLinks: EnvironmentAppLink[] = [
    { label: 'Work Office', url: environment.appUrls.workOffice },
    { label: 'Office (v4)', url: environment.appUrls.workOfficeV4 },
    { label: 'App', url: environment.appUrls.app },
    { label: 'Gerüstrechner', url: environment.appUrls.geruestrechner },
    { label: 'Terminassistent', url: environment.appUrls.appointmentScheduler },
    { label: 'Website', url: environment.appUrls.website },
  ].filter((appLink) => !!appLink.url);

  protected readonly isModalOpen = signal(false);
  protected readonly isLoadingEnvironmentInfo = signal(false);
  protected readonly hasEnvironmentInfoError = signal(false);
  protected readonly environmentInfo = signal<WorkEnvironmentInfoDto | null>(null);

  /**
   * Umleitungs-Adressen als fertiger Text, damit im Template keine Funktion aufgerufen werden muss.
   */
  protected readonly mailFallbackAddressesText = computed(() => this.environmentInfo()?.mailFallbackAddresses.join(', ') ?? '');

  private readonly workEnvironmentService = inject(WorkEnvironmentService);

  @HostListener('document:keydown.escape')
  protected closeModal(): void {
    this.isModalOpen.set(false);
  }

  protected async openModal(): Promise<void> {
    this.isModalOpen.set(true);
    if (this.environmentInfo() || this.isLoadingEnvironmentInfo()) {
      return;
    }
    this.isLoadingEnvironmentInfo.set(true);
    this.hasEnvironmentInfoError.set(false);
    try {
      const environmentInfo = await lastValueFrom(
        this.workEnvironmentService.environmentV2ControllerGetEnvironmentInfo({
          'x-api-key': environment.apiConfiguration.publicApiKey,
        }),
      );
      this.environmentInfo.set(environmentInfo);
    } catch (getEnvironmentInfoException) {
      this.hasEnvironmentInfoError.set(true);
    } finally {
      this.isLoadingEnvironmentInfo.set(false);
    }
  }
}
